import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/** Room kept on the left for the round button at the top left (the
 *  settings cog at home, its 14px inset and 44px; the map button's ring over
 *  a slot, its 10px inset and 52px), and a small gap. The map button's plate
 *  reaches further, but measures the pill and stops short of it. */
const LEFT_BUTTON_CLEAR = 14 + 44 + 8;

export interface WalletRowProps {
  /** The wallet pill, given the room it has (CSS px; null until
   *  measured). */
  children: (maxWidth: number | null) => ReactNode;
}

/**
 * The wallet's row along the top of the screen, right-aligned, from just
 * right of the round button at the top left to the right edge. It tells the
 * pill how much room it has, so on a narrow phone the pill tightens or
 * drops chips rather than running under the button. The pill has the whole
 * row (the slot's favourite heart that sat left of it is gone).
 */
export function WalletRow({ children }: WalletRowProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState<number | null>(null);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;
    const measure = () => setRoom(Math.max(0, Math.floor(row.clientWidth)));
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(row);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <div
      ref={rowRef}
      data-testid="wallet-row"
      style={{
        position: 'absolute',
        // iOS pre-flight fix (2026-06-07): below the Dynamic Island, which
        // Capacitor reports as the top safe-area inset (0 on the web).
        top: 'calc(env(safe-area-inset-top, 0px) + 4px)',
        left: `calc(env(safe-area-inset-left, 0px) + ${LEFT_BUTTON_CLEAR}px)`,
        right: 'calc(env(safe-area-inset-right, 0px) + 18px)',
        zIndex: 45,
        // Taps fall through to the scene; the pill's chips take their own.
        pointerEvents: 'none',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
      }}
    >
      {children(room)}
    </div>
  );
}
